import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import i18next from 'i18next';

import * as api from 'services/ErplyAPI/clockInOut';
import { lastClockInTime } from 'actions/clockInOut';
import { getSelectedPos, getSelectedPosID } from 'reducers/PointsOfSale';
import {
  getHasRightToOpenAndCloseDay,
  getClientCode,
  getUserLoggedIn,
} from 'reducers/Login';
import {
  lastInvoiceNumbers,
  lastReceiptData,
  pendingRequests,
} from 'services/localDB';
import { checkIsDayOpen } from 'actions/OpenCloseDay';
import {
  abortSyncLocalDB,
  syncLocalDatabaseSO,
  syncLocalDB,
  syncPendingRequest,
} from 'actions/connectivity';
import { getAllWarehouses, getLoggedInWarehouse } from 'reducers/warehouses';
import { integrationFunctions as getIntegrationFunctions } from 'actions/integrations/terminal';
import { pluginCleanupOnUnmount } from 'actions/Plugins';
import {
  getDisabledDatabaseSO,
  getFetchOnlyProductsThatAreInStock,
  getSettingsAreLoaded,
} from 'reducers/configs/settings';
import { addError, dismissType, addWarning } from 'actions/Error';
import './Main.css';
import { getHasDayOpen } from 'reducers/OpenCloseDay';
import { UrlControl } from 'services/UrlControl/UrlControl';
import { useIsLoggedIn } from 'containers/App/hooks/useIsLoggedIn';
import { SO } from 'services/DB/types';
import { stopSync } from 'services/DB/syncDB';
import { getDB, resetDB } from 'services/DB';
import { REDUX_FETCH_ONLY_PRODUCTS_IN_STOCK } from 'constants/persistence';
import { UrlControlActions } from 'services/UrlControl/actions';
import { GridButtonSizeProvider } from 'components/GridButton';
import { selectPos } from 'actions/PointsOfSale/selectPos';
import { redirectToLoginApp } from 'services/ErplyAPI/redirectToLoginApp';
import {
  getIsLoadingEssentialData,
  getShouldFetchEssentialData,
} from 'reducers/essentialData';

const useUpdateLocalProducts = () => {
  const settingsLoaded = useSelector(getSettingsAreLoaded);
  const fetchOnlyProductsInStock = useSelector(
    getFetchOnlyProductsThatAreInStock,
  );
  const clientCode = useSelector(getClientCode);
  const isLoggedIn = useIsLoggedIn();
  const dispatch = useDispatch();

  const checkIfNeedsUpdating = useCallback(async () => {
    if (!isLoggedIn) {
      return false;
    }
    if (!settingsLoaded) {
      return false;
    }
    const db = await getDB(clientCode);
    const idbCurrentlyHoldsStockOnly = await db.get(
      SO.META.NAME,
      REDUX_FETCH_ONLY_PRODUCTS_IN_STOCK,
    );

    return fetchOnlyProductsInStock !== idbCurrentlyHoldsStockOnly;
  }, [clientCode, fetchOnlyProductsInStock, isLoggedIn, settingsLoaded]);

  const resyncProducts = useCallback(async () => {
    const needsUpdating = await checkIfNeedsUpdating();
    if (!needsUpdating) return;

    const name = SO.PRODUCTS.NAME;
    await getDB(clientCode).then(db =>
      db.put(
        SO.META.NAME,
        fetchOnlyProductsInStock,
        REDUX_FETCH_ONLY_PRODUCTS_IN_STOCK,
      ),
    );

    await stopSync(name);
    await resetDB(clientCode, name);
    dispatch(syncLocalDatabaseSO(name));
  }, [checkIfNeedsUpdating, clientCode, fetchOnlyProductsInStock, dispatch]);

  useEffect(() => {
    resyncProducts();
  }, [resyncProducts]);
};

const onLogin = () => async (dispatch, getState) => {
  if (UrlControl.actionReturnSale) {
    i18next.loadNamespaces('urlControl').then(() => {
      dispatch(
        addWarning(i18next.t('urlControl:actionProgress.return'), {
          errorType: 'UrlControl',
          dismissible: false,
          selfDismiss: false,
        }),
      );
    });
  }
  if (UrlControl.actionCancelLayaway) {
    i18next.loadNamespaces('urlControl').then(() => {
      dispatch(
        addWarning(i18next.t('urlControl:actionProgress.cancelLayaway'), {
          errorType: 'UrlControl',
          dismissible: false,
          selfDismiss: false,
        }),
      );
    });
  }
  dispatch(syncPendingRequest());
  dispatch(checkIsDayOpen({ pointOfSaleID: getSelectedPosID(getState()) }));
  api
    .clockedInEmployees({
      warehouseIDs: getSelectedPos(getState())?.warehouseID,
    })
    .then(response => {
      const employeeIsClockedIn = response.find(
        employee =>
          employee.employeeID ===
          parseInt(getUserLoggedIn(getState())?.employeeID, 10),
      );
      if (employeeIsClockedIn) {
        dispatch(lastClockInTime());
      }
    })
    .catch(e =>
      console.error(
        'Failed to fetch clocked-in employees (not enough permissions?)\n',
        e,
      ),
    );
  const clientCode = getClientCode(getState());

  const warehouse = getLoggedInWarehouse(getState());
  if (!warehouse) {
    dispatch(addError(i18next.t('alerts:errors.missingWarehouse')));
    console.error(
      'Stopping login early because data not present - this can happen if logging in/out very quickly',
    );
    return dispatch(selectPos(null));
  }

  // TODO: Add following localForage dbs to BrazilPOS IDB
  // TODO: remove option in settings for supporting offline customer support
  // TODO: !!! correction, only customers fetched during search can be stored locally but we should not sync all customers!!!
  pendingRequests.initiate({ CLIENT_CODE: clientCode });
  lastInvoiceNumbers.initiate({ CLIENT_CODE: clientCode });
  lastReceiptData.initiate({ CLIENT_CODE: clientCode }).then(() => {
    lastReceiptData.reset();
  });
  lastInvoiceNumbers.setInitialNumber(getSelectedPosID(getState()));

  // UrlControl automations
  dispatch(dismissType('UrlControl'));
  dispatch(UrlControlActions.initRelevantPayment);
};

const isPnPDomain =
  /\/brazil-pos\/PnP-UAT/.test(window.location.href) ||
  window.origin === 'https://eposp.erply.com';

const onLogout = () => async dispatch => {
  // Cleanup for payment integration
  dispatch(getIntegrationFunctions()).then(data => {
    if (data?.integrationCleanup) data.integrationCleanup();
  });

  // clean up for enabled plugins
  dispatch(pluginCleanupOnUnmount());

  localStorage.removeItem('customers');

  // TODO: GET RID OF THIS PNP-SPECIFIC HACK
  if (isPnPDomain) redirectToLoginApp();
};

const Main = ({ header, main }) => {
  const dispatch = useDispatch();
  const disabledDatabaseSO = useSelector(getDisabledDatabaseSO);
  const hasDayOpen = useSelector(getHasDayOpen);
  const hasRightsToOpenDay = useSelector(getHasRightToOpenAndCloseDay);
  const isLoggedIn = useIsLoggedIn();

  const warehouses = useSelector(getAllWarehouses);
  const warehousesFetched = warehouses.length > 0;
  const settingsFetched = useSelector(getSettingsAreLoaded);
  const isEssentialDataLoading = useSelector(getIsLoadingEssentialData);
  const shouldFetchEssentialData = useSelector(getShouldFetchEssentialData);

  const shouldRenderEssentialDataLoader =
    isEssentialDataLoading || shouldFetchEssentialData;
  const shouldRenderPos =
    isLoggedIn && (hasDayOpen || (!hasDayOpen && hasRightsToOpenDay));

  useEffect(() => {
    if (shouldRenderPos && !shouldRenderEssentialDataLoader) {
      dispatch(onLogin());
      return () => {
        dispatch(onLogout());
      };
    }
    return () => {}; // for consistent-return eslint rule
  }, [
    dispatch,
    shouldRenderEssentialDataLoader,
    isEssentialDataLoading,
    shouldFetchEssentialData,
    shouldRenderPos,
  ]);

  useDeepCompareEffect(() => {
    if (isLoggedIn && warehousesFetched && settingsFetched) {
      dispatch(syncLocalDB());
      return () => {
        dispatch(abortSyncLocalDB());
      };
    }
  }, [disabledDatabaseSO, isLoggedIn, warehousesFetched, settingsFetched]);

  useUpdateLocalProducts();

  return (
    <div className="erply-container">
      <div className="erply-header" data-testid="erply-header">
        {header}
      </div>
      {shouldRenderPos && <div data-portal="urgent-alert-container" />}
      <div className="erply-main">
        <GridButtonSizeProvider>{main}</GridButtonSizeProvider>
      </div>
    </div>
  );
};

export default React.memo(Main);
